/**
 * Nova Derma — canonical enum values.
 *
 * THIS FILE IS THE SINGLE SOURCE OF TRUTH for every enum string used across the
 * system. The Prisma schema MUST declare enums with exactly these member names,
 * and the web client MUST import from here rather than re-declaring literals.
 *
 * Frozen file — do not edit during feature work.
 */

// ---------------------------------------------------------------------------
// Identity & access
// ---------------------------------------------------------------------------

/** Built-in role slugs (PRD §3). Roles themselves are user-buildable (SEC-01). */
export const SystemRole = {
  ADMIN: 'ADMIN',
  RECEPTIONIST: 'RECEPTIONIST',
  DOCTOR: 'DOCTOR',
  SPECIALIST: 'SPECIALIST',
  ACCOUNTANT: 'ACCOUNTANT',
  DOCTOR_ASSISTANT: 'DOCTOR_ASSISTANT',
} as const;
export type SystemRole = (typeof SystemRole)[keyof typeof SystemRole];

/** PROV-01 — provider kind. Drives commission eligibility (COST-09). */
export const ProviderType = {
  DOCTOR: 'DOCTOR',
  SPECIALIST: 'SPECIALIST',
  TECHNICIAN: 'TECHNICIAN',
} as const;
export type ProviderType = (typeof ProviderType)[keyof typeof ProviderType];

/** SEC-04 — audit log records changes only, never reads. */
export const AuditAction = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  POST: 'POST',
  VOID: 'VOID',
  APPROVE: 'APPROVE',
  LOCK: 'LOCK',
  MERGE: 'MERGE',
  OVERRIDE: 'OVERRIDE',
  LOGIN: 'LOGIN',
} as const;
export type AuditAction = (typeof AuditAction)[keyof typeof AuditAction];

export const Language = { AR: 'AR', EN: 'EN' } as const;
export type Language = (typeof Language)[keyof typeof Language];

export const Gender = { MALE: 'MALE', FEMALE: 'FEMALE', OTHER: 'OTHER' } as const;
export type Gender = (typeof Gender)[keyof typeof Gender];

// ---------------------------------------------------------------------------
// Scheduling (PRD §5.3)
// ---------------------------------------------------------------------------

/**
 * APT-06 natural path: SCHEDULED → CONFIRMED → ARRIVED → IN_SESSION → COMPLETED
 * APT-07 side paths: NO_ANSWER, CANCELLED_BY_CLINIC, CANCELLED_BY_PATIENT,
 *                    NO_SHOW, RESCHEDULED
 * APT-08: NO_SHOW is never set automatically — a human closes it.
 */
export const AppointmentStatus = {
  PENDING: 'PENDING',
  SCHEDULED: 'SCHEDULED',
  CONFIRMED: 'CONFIRMED',
  ARRIVED: 'ARRIVED',
  IN_SESSION: 'IN_SESSION',
  COMPLETED: 'COMPLETED',
  NO_ANSWER: 'NO_ANSWER',
  CANCELLED_BY_CLINIC: 'CANCELLED_BY_CLINIC',
  CANCELLED_BY_PATIENT: 'CANCELLED_BY_PATIENT',
  NO_SHOW: 'NO_SHOW',
  REJECTED: 'REJECTED',
  RESCHEDULED: 'RESCHEDULED',
} as const;
export type AppointmentStatus = (typeof AppointmentStatus)[keyof typeof AppointmentStatus];

/** How an appointment entered the clinic schedule. */
export const AppointmentSource = { STAFF: 'STAFF', ONLINE: 'ONLINE' } as const;
export type AppointmentSource = (typeof AppointmentSource)[keyof typeof AppointmentSource];

export const OnlineBookingFlowMode = { INSTANT: 'INSTANT', REQUEST: 'REQUEST', CALLBACK: 'CALLBACK', STAGED: 'STAGED' } as const;
export type OnlineBookingFlowMode = (typeof OnlineBookingFlowMode)[keyof typeof OnlineBookingFlowMode];

/** Statuses that count as a "completed visit" for the new-patient definition (§4). */
export const COMPLETED_APPOINTMENT_STATUSES: AppointmentStatus[] = [AppointmentStatus.COMPLETED];

/** Statuses that occupy a calendar slot. */
export const ACTIVE_APPOINTMENT_STATUSES: AppointmentStatus[] = [
  AppointmentStatus.PENDING,
  AppointmentStatus.SCHEDULED,
  AppointmentStatus.CONFIRMED,
  AppointmentStatus.ARRIVED,
  AppointmentStatus.IN_SESSION,
];

/** Statuses that release the slot. */
export const CANCELLED_APPOINTMENT_STATUSES: AppointmentStatus[] = [
  AppointmentStatus.CANCELLED_BY_CLINIC,
  AppointmentStatus.CANCELLED_BY_PATIENT,
  AppointmentStatus.RESCHEDULED,
];

/** Statuses that never reserve availability, including requests reception rejected. */
export const NON_OCCUPYING_APPOINTMENT_STATUSES: AppointmentStatus[] = [
  ...CANCELLED_APPOINTMENT_STATUSES,
  AppointmentStatus.REJECTED,
];

/** CAL-07 — what drives appointment card colour. */
export const CalendarColorMode = {
  PROCEDURE: 'PROCEDURE',
  STATUS: 'STATUS',
  PROVIDER: 'PROVIDER',
} as const;
export type CalendarColorMode = (typeof CalendarColorMode)[keyof typeof CalendarColorMode];

/**
 * CAL-13/CAL-14 — what the day-view column header leads with. The columns stay
 * one-per-provider either way: PROV-02 makes the room a label copied onto the
 * appointment with no conflict checking, so it can name a column but never
 * own one.
 */
export const CalendarHeaderMode = {
  PROVIDER: 'PROVIDER',
  ROOM: 'ROOM',
} as const;
export type CalendarHeaderMode = (typeof CalendarHeaderMode)[keyof typeof CalendarHeaderMode];

export const CalendarView = {
  DAY: 'DAY',
  WEEK: 'WEEK',
  MONTH: 'MONTH',
  AGENDA: 'AGENDA',
} as const;
export type CalendarView = (typeof CalendarView)[keyof typeof CalendarView];

/** APT-10 — recall is a flag on the patient, not an appointment status. */
export const RecallStatus = {
  OPEN: 'OPEN',
  BOOKED: 'BOOKED',
  DISMISSED: 'DISMISSED',
} as const;
export type RecallStatus = (typeof RecallStatus)[keyof typeof RecallStatus];

export const TaskStatus = { OPEN: 'OPEN', DONE: 'DONE', CANCELLED: 'CANCELLED' } as const;
export type TaskStatus = (typeof TaskStatus)[keyof typeof TaskStatus];

// ---------------------------------------------------------------------------
// Inventory (PRD §5.1)
// ---------------------------------------------------------------------------

/** INV-63 — item kind. OPERATIONAL is never allocated to a session. */
export const ItemType = {
  /** مادة مباشرة — cost is charged to the patient/session. */
  DIRECT_MATERIAL: 'DIRECT_MATERIAL',
  /** مستهلك تشغيلي — charged to the clinic, never split across sessions. */
  OPERATIONAL: 'OPERATIONAL',
  /** صنف للبيع للمريض */
  RETAIL: 'RETAIL',
} as const;
export type ItemType = (typeof ItemType)[keyof typeof ItemType];

/** INV-64 — costing/tracking method, configurable per item. */
export const TrackingMethod = {
  /** دفعة (Lot) — batch number, expiry and independent cost. */
  BATCH: 'BATCH',
  /** متوسط تكلفة مرجّح */
  WEIGHTED_AVERAGE: 'WEIGHTED_AVERAGE',
} as const;
export type TrackingMethod = (typeof TrackingMethod)[keyof typeof TrackingMethod];

/**
 * INV-81/86 — what happens to what is LEFT of a material once it has been
 * opened for one patient. Opening a 100-unit vial to inject 20 does not say what
 * the other 80 are, and neither answer may be assumed, so it is set per item.
 */
export const MaterialLeftoverPolicy = {
  /** الباقي يرجع للرف — the remainder serves the next patient from its own lot. */
  REUSABLE: 'REUSABLE',
  /** خاص بالمريض — the remainder is discarded against the session that opened it. */
  SINGLE_PATIENT: 'SINGLE_PATIENT',
} as const;
export type MaterialLeftoverPolicy =
  (typeof MaterialLeftoverPolicy)[keyof typeof MaterialLeftoverPolicy];

/** INV-30 — unit kind, prevents illogical purchase/consumption units. */
export const UnitType = { COUNT: 'COUNT', VOLUME: 'VOLUME', WEIGHT: 'WEIGHT' } as const;
export type UnitType = (typeof UnitType)[keyof typeof UnitType];

/** INV-50 — variant attribute value kind. */
export const AttributeValueType = {
  LIST: 'LIST',
  NUMBER: 'NUMBER',
  TEXT: 'TEXT',
} as const;
export type AttributeValueType = (typeof AttributeValueType)[keyof typeof AttributeValueType];

/** INV-80 — the only four issue movement kinds, all through one ledger. */
export const StockMovementType = {
  /** Receipt from a posted purchase invoice. */
  PURCHASE_IN: 'PURCHASE_IN',
  /** استهلاك جلسة — charged to the patient. */
  SESSION_CONSUMPTION: 'SESSION_CONSUMPTION',
  /** استهلاك عام — charged to the clinic. */
  GENERAL_CONSUMPTION: 'GENERAL_CONSUMPTION',
  /** هدر وإتلاف */
  WASTAGE: 'WASTAGE',
  /** مرتجع لمورد */
  SUPPLIER_RETURN: 'SUPPLIER_RETURN',
  /** INV-90/91 — stocktake adjustment, either direction. */
  STOCKTAKE_ADJUSTMENT: 'STOCKTAKE_ADJUSTMENT',
} as const;
export type StockMovementType = (typeof StockMovementType)[keyof typeof StockMovementType];

/** Movement types that decrease stock. */
export const OUTBOUND_MOVEMENT_TYPES: StockMovementType[] = [
  StockMovementType.SESSION_CONSUMPTION,
  StockMovementType.GENERAL_CONSUMPTION,
  StockMovementType.WASTAGE,
  StockMovementType.SUPPLIER_RETURN,
];

/** INV-84 — consumption against zero stock is recorded and flagged, never blocked. */
export const MovementSettlementStatus = {
  SETTLED: 'SETTLED',
  /** بانتظار تسوية */
  PENDING_SETTLEMENT: 'PENDING_SETTLEMENT',
} as const;
export type MovementSettlementStatus =
  (typeof MovementSettlementStatus)[keyof typeof MovementSettlementStatus];

/** INV-76 — purchase invoice lifecycle. */
export const PurchaseInvoiceStatus = {
  DRAFT: 'DRAFT',
  POSTED: 'POSTED',
  CANCELLED: 'CANCELLED',
} as const;
export type PurchaseInvoiceStatus =
  (typeof PurchaseInvoiceStatus)[keyof typeof PurchaseInvoiceStatus];

/** INV-101 — in-app bell alert kinds (no WhatsApp, no email). */
export const StockAlertType = {
  BELOW_REORDER_POINT: 'BELOW_REORDER_POINT',
  NEAR_EXPIRY: 'NEAR_EXPIRY',
  EXPIRED: 'EXPIRED',
  /** صنف بدون حركة من X شهر — dormant money. */
  DORMANT: 'DORMANT',
  PENDING_SETTLEMENT: 'PENDING_SETTLEMENT',
} as const;
export type StockAlertType = (typeof StockAlertType)[keyof typeof StockAlertType];

/** INV-72 — line-level discount kind. Shared with billing (BIL-01). */
export const DiscountType = { PERCENT: 'PERCENT', AMOUNT: 'AMOUNT' } as const;
export type DiscountType = (typeof DiscountType)[keyof typeof DiscountType];

// ---------------------------------------------------------------------------
// Billing (PRD §5.8)
// ---------------------------------------------------------------------------

export const InvoiceStatus = {
  DRAFT: 'DRAFT',
  ISSUED: 'ISSUED',
  PARTIALLY_PAID: 'PARTIALLY_PAID',
  PAID: 'PAID',
  VOIDED: 'VOIDED',
  REFUNDED: 'REFUNDED',
} as const;
export type InvoiceStatus = (typeof InvoiceStatus)[keyof typeof InvoiceStatus];

export const PaymentPayerType = { PATIENT: 'PATIENT', INSURER: 'INSURER' } as const;
export type PaymentPayerType = (typeof PaymentPayerType)[keyof typeof PaymentPayerType];

/** BIL-10 — JoFotara is out of scope for v1 but the invoice carries the fields. */
export const EInvoiceStatus = {
  NOT_SUBMITTED: 'NOT_SUBMITTED',
  QUEUED: 'QUEUED',
  SUBMITTED: 'SUBMITTED',
  ACCEPTED: 'ACCEPTED',
  REJECTED: 'REJECTED',
} as const;
export type EInvoiceStatus = (typeof EInvoiceStatus)[keyof typeof EInvoiceStatus];

/** APT-12 — a retouch is a standalone zero-revenue session with a stated reason. */
export const ExemptionReason = {
  NONE: 'NONE',
  RETOUCH: 'RETOUCH',
  WARRANTY: 'WARRANTY',
  COMPLIMENTARY: 'COMPLIMENTARY',
} as const;
export type ExemptionReason = (typeof ExemptionReason)[keyof typeof ExemptionReason];

// ---------------------------------------------------------------------------
// Packages
// ---------------------------------------------------------------------------

/**
 * How a package is paid for, chosen by the admin per package:
 *  - PREPAID         — the full bundle price is collected at sale; sessions are
 *                      then consumed with no further charge.
 *  - PAY_PER_SESSION — the bundle price is split across its sessions; each
 *                      session is charged its allocated share when performed.
 *  - FLEXIBLE        — payment and consumption are not tied: the patient may
 *                      pay in full, partially or later while sessions continue;
 *                      the package total, payments and balance are tracked.
 */
export const PackageBillingMode = {
  PREPAID: 'PREPAID',
  PAY_PER_SESSION: 'PAY_PER_SESSION',
  FLEXIBLE: 'FLEXIBLE',
} as const;
export type PackageBillingMode = (typeof PackageBillingMode)[keyof typeof PackageBillingMode];

/** A purchased package: active while sessions remain, cancelled or completed at the end. */
export const PatientPackageStatus = {
  ACTIVE: 'ACTIVE',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const;
export type PatientPackageStatus =
  (typeof PatientPackageStatus)[keyof typeof PatientPackageStatus];

// ---------------------------------------------------------------------------
// Costing & commissions (PRD §5.2)
// ---------------------------------------------------------------------------

/**
 * COST-04 — four-level rate hierarchy, most specific wins.
 * CLINIC → PROCEDURE_CATEGORY → PROCEDURE → PROVIDER_PROCEDURE
 */
export const CommissionRateScope = {
  CLINIC: 'CLINIC',
  PROCEDURE_CATEGORY: 'PROCEDURE_CATEGORY',
  PROCEDURE: 'PROCEDURE',
  PROVIDER_PROCEDURE: 'PROVIDER_PROCEDURE',
} as const;
export type CommissionRateScope =
  (typeof CommissionRateScope)[keyof typeof CommissionRateScope];

/** DEC-05/11 — the price basis frozen on each effective commission rate. */
export const CommissionDiscountBasis = {
  BEFORE_DISCOUNT: 'BEFORE_DISCOUNT',
  AFTER_DISCOUNT: 'AFTER_DISCOUNT',
} as const;
export type CommissionDiscountBasis =
  (typeof CommissionDiscountBasis)[keyof typeof CommissionDiscountBasis];

/** DEC-05 — whether commission uses session revenue before or after materials. */
export const CommissionMaterialCostBasis = {
  BEFORE_MATERIAL_COST: 'BEFORE_MATERIAL_COST',
  AFTER_MATERIAL_COST: 'AFTER_MATERIAL_COST',
} as const;
export type CommissionMaterialCostBasis =
  (typeof CommissionMaterialCostBasis)[keyof typeof CommissionMaterialCostBasis];

/** Specificity order used to resolve the effective rate (COST-05 shows the source). */
export const COMMISSION_SCOPE_SPECIFICITY: Record<CommissionRateScope, number> = {
  CLINIC: 0,
  PROCEDURE_CATEGORY: 1,
  PROCEDURE: 2,
  PROVIDER_PROCEDURE: 3,
};

/** COST-12 — statement lifecycle; frozen permanently after approval. */
export const CommissionStatementStatus = {
  DRAFT: 'DRAFT',
  APPROVED: 'APPROVED',
  PAID: 'PAID',
} as const;
export type CommissionStatementStatus =
  (typeof CommissionStatementStatus)[keyof typeof CommissionStatementStatus];

/** COST-13 — a correction to a locked period lands on the next statement. */
export const CommissionLineKind = {
  REGULAR: 'REGULAR',
  /** تسوية عن شهر سابق */
  PRIOR_PERIOD_ADJUSTMENT: 'PRIOR_PERIOD_ADJUSTMENT',
} as const;
export type CommissionLineKind = (typeof CommissionLineKind)[keyof typeof CommissionLineKind];

/**
 * PRD §9 Q1 — who absorbs the discount in the commission calculation.
 * Default PROPORTIONAL is option (أ): the discount reduces net profit, so provider
 * and clinic each lose their share. It is the natural outcome of the COST-01 formula.
 */
export const DiscountBearer = {
  /** (أ) الطرفين بالتناسب — default. */
  PROPORTIONAL: 'PROPORTIONAL',
  /** (ب) الطبيب لحاله — clinic takes its share as if no discount happened. */
  PROVIDER: 'PROVIDER',
  /** (ج) العيادة لحالها — provider is paid on the pre-discount price. */
  CLINIC: 'CLINIC',
} as const;
export type DiscountBearer = (typeof DiscountBearer)[keyof typeof DiscountBearer];

export const ExpenseRecurrence = {
  ONE_TIME: 'ONE_TIME',
  MONTHLY: 'MONTHLY',
} as const;
export type ExpenseRecurrence = (typeof ExpenseRecurrence)[keyof typeof ExpenseRecurrence];

// ---------------------------------------------------------------------------
// Messaging (PRD §5.4)
// ---------------------------------------------------------------------------

export const MessageChannel = { SMS: 'SMS' } as const;
export type MessageChannel = (typeof MessageChannel)[keyof typeof MessageChannel];

export const MessageTrigger = {
  ONLINE_BOOKING_REQUEST: 'ONLINE_BOOKING_REQUEST',
  ONLINE_BOOKING_ACCEPTED: 'ONLINE_BOOKING_ACCEPTED',
  ONLINE_BOOKING_REJECTED: 'ONLINE_BOOKING_REJECTED',
  BOOKING_CONFIRMATION: 'BOOKING_CONFIRMATION',
  APPOINTMENT_REMINDER: 'APPOINTMENT_REMINDER',
  FOLLOW_UP_REMINDER: 'FOLLOW_UP_REMINDER',
  BALANCE_REMINDER: 'BALANCE_REMINDER',
  /** MSG-04 / APT-15 — manual only, never automatic. */
  POST_SESSION: 'POST_SESSION',
  MANUAL: 'MANUAL',
  CAMPAIGN: 'CAMPAIGN',
  INVOICE_LINK: 'INVOICE_LINK',
} as const;
export type MessageTrigger = (typeof MessageTrigger)[keyof typeof MessageTrigger];

export const MessageStatus = {
  QUEUED: 'QUEUED',
  SENT: 'SENT',
  DELIVERED: 'DELIVERED',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
} as const;
export type MessageStatus = (typeof MessageStatus)[keyof typeof MessageStatus];

/** MSG-03 — the confirm/cancel link flips the appointment status by itself. */
export const ConfirmationLinkAction = {
  CONFIRM: 'CONFIRM',
  CANCEL: 'CANCEL',
} as const;
export type ConfirmationLinkAction =
  (typeof ConfirmationLinkAction)[keyof typeof ConfirmationLinkAction];

export const CampaignStatus = {
  DRAFT: 'DRAFT',
  SCHEDULED: 'SCHEDULED',
  SENDING: 'SENDING',
  SENT: 'SENT',
  CANCELLED: 'CANCELLED',
} as const;
export type CampaignStatus = (typeof CampaignStatus)[keyof typeof CampaignStatus];

// ---------------------------------------------------------------------------
// Clinical (PRD §5.5–5.7)
// ---------------------------------------------------------------------------

/** PAT-22 — Fitzpatrick skin type drives laser settings and prevents burns. */
export const SkinType = {
  I: 'I',
  II: 'II',
  III: 'III',
  IV: 'IV',
  V: 'V',
  VI: 'VI',
} as const;
export type SkinType = (typeof SkinType)[keyof typeof SkinType];

export const PregnancyStatus = {
  NOT_APPLICABLE: 'NOT_APPLICABLE',
  NO: 'NO',
  PREGNANT: 'PREGNANT',
  BREASTFEEDING: 'BREASTFEEDING',
} as const;
export type PregnancyStatus = (typeof PregnancyStatus)[keyof typeof PregnancyStatus];

export const VisitStatus = {
  OPEN: 'OPEN',
  COMPLETED: 'COMPLETED',
} as const;
export type VisitStatus = (typeof VisitStatus)[keyof typeof VisitStatus];

/** CLIN-01 — whether reusable section answers belong to one session or patient. */
export const ClinicalSectionScope = {
  DESIGN_ONLY: 'DESIGN_ONLY',
  PATIENT_SHARED: 'PATIENT_SHARED',
} as const;
export type ClinicalSectionScope =
  (typeof ClinicalSectionScope)[keyof typeof ClinicalSectionScope];

/** Lifecycle of a stable clinical configuration identity. */
export const ClinicalIdentityStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  RETIRED: 'RETIRED',
} as const;
export type ClinicalIdentityStatus =
  (typeof ClinicalIdentityStatus)[keyof typeof ClinicalIdentityStatus];

/** Lifecycle of an immutable clinical configuration revision. */
export const ClinicalVersionState = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
  ABANDONED: 'ABANDONED',
} as const;
export type ClinicalVersionState =
  (typeof ClinicalVersionState)[keyof typeof ClinicalVersionState];

/** Where a clinical configuration revision came from. */
export const ClinicalVersionSource = {
  ADMIN_CREATED: 'ADMIN_CREATED',
  BACKFILL_CURRENT: 'BACKFILL_CURRENT',
  LIBRARY_INSTALL: 'LIBRARY_INSTALL',
  PACK_INSTALL: 'PACK_INSTALL',
} as const;
export type ClinicalVersionSource =
  (typeof ClinicalVersionSource)[keyof typeof ClinicalVersionSource];

/** How a batch-tracked clinical material is allocated. */
export const ClinicalBatchSelectionMode = {
  EXPLICIT: 'EXPLICIT',
  AUTO_FEFO: 'AUTO_FEFO',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
} as const;
export type ClinicalBatchSelectionMode =
  (typeof ClinicalBatchSelectionMode)[keyof typeof ClinicalBatchSelectionMode];

/** Source of the product selected for one saved region. */
export const ClinicalProductSelectionSource = {
  SESSION_DEFAULT: 'SESSION_DEFAULT',
  REGION_OVERRIDE: 'REGION_OVERRIDE',
} as const;
export type ClinicalProductSelectionSource =
  (typeof ClinicalProductSelectionSource)[keyof typeof ClinicalProductSelectionSource];

/** Source of the batch allocation policy recorded for one region. */
export const ClinicalBatchSelectionSource = {
  SESSION_DEFAULT: 'SESSION_DEFAULT',
  REGION_OVERRIDE: 'REGION_OVERRIDE',
  AUTOMATIC: 'AUTOMATIC',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
} as const;
export type ClinicalBatchSelectionSource =
  (typeof ClinicalBatchSelectionSource)[keyof typeof ClinicalBatchSelectionSource];

/** Where a group item is offered: on its map's drawing, or as a plain selection. */
export const RegionPresentation = { MAP: 'MAP', LIST: 'LIST' } as const;
export type RegionPresentation = (typeof RegionPresentation)[keyof typeof RegionPresentation];

/** CLIN-11 — the SVG map library we ship. The admin never draws new ones (CLIN-14). */
export const BodyMapKind = {
  FACE_FRONT: 'FACE_FRONT',
  FACE_LEFT: 'FACE_LEFT',
  FACE_RIGHT: 'FACE_RIGHT',
  SCALP: 'SCALP',
  BODY_FRONT: 'BODY_FRONT',
  BODY_BACK: 'BODY_BACK',
  HANDS: 'HANDS',
  NECK_CHEST: 'NECK_CHEST',
  REGION_LIST: 'REGION_LIST',
} as const;
export type BodyMapKind = (typeof BodyMapKind)[keyof typeof BodyMapKind];

/** CLIN-12 — left and right are always distinct because dosing differs. */
export const BodySide = {
  LEFT: 'LEFT',
  RIGHT: 'RIGHT',
  CENTER: 'CENTER',
} as const;
export type BodySide = (typeof BodySide)[keyof typeof BodySide];

/** CLIN-15 — what gets recorded on a region depends on the procedure. */
export const RegionMetricKind = {
  /** Botox — units. */
  UNITS: 'UNITS',
  /** Filler — ml + product + needle/cannula. */
  VOLUME_ML: 'VOLUME_ML',
  /** Laser — fluence + pulse count. */
  LASER_SETTINGS: 'LASER_SETTINGS',
  /** Cryo — cycles + freeze duration. */
  CRYO_CYCLES: 'CRYO_CYCLES',
  /** Free-form JSON for builder-defined procedures. */
  CUSTOM: 'CUSTOM',
} as const;
export type RegionMetricKind = (typeof RegionMetricKind)[keyof typeof RegionMetricKind];

export const InjectionTechnique = {
  NEEDLE: 'NEEDLE',
  CANNULA: 'CANNULA',
} as const;
export type InjectionTechnique =
  (typeof InjectionTechnique)[keyof typeof InjectionTechnique];

/** PAT-31 — consent form element kinds. */
export const ConsentFieldType = {
  STATIC_TEXT: 'STATIC_TEXT',
  YES_NO: 'YES_NO',
  MULTIPLE_CHOICE: 'MULTIPLE_CHOICE',
  FREE_TEXT: 'FREE_TEXT',
  DATE: 'DATE',
  ACKNOWLEDGEMENT: 'ACKNOWLEDGEMENT',
  SIGNATURE: 'SIGNATURE',
} as const;
export type ConsentFieldType = (typeof ConsentFieldType)[keyof typeof ConsentFieldType];

/** PAT-25 — before/after album, guarded by its own permission. */
export const PhotoPhase = {
  BEFORE: 'BEFORE',
  AFTER: 'AFTER',
  DURING: 'DURING',
} as const;
export type PhotoPhase = (typeof PhotoPhase)[keyof typeof PhotoPhase];

/** Paperwork filed beside the photo album. */
export const PatientDocumentCategory = {
  LAB_REPORT: 'LAB_REPORT',
  MEDICAL_REPORT: 'MEDICAL_REPORT',
  REFERRAL: 'REFERRAL',
  PRESCRIPTION: 'PRESCRIPTION',
  CONSENT: 'CONSENT',
  INSURANCE: 'INSURANCE',
  IDENTITY: 'IDENTITY',
  OTHER: 'OTHER',
} as const;
export type PatientDocumentCategory =
  (typeof PatientDocumentCategory)[keyof typeof PatientDocumentCategory];

// ---------------------------------------------------------------------------
// Labs & pharmacy (PRD §5.7)
// ---------------------------------------------------------------------------

export const LabOrderStatus = {
  ORDERED: 'ORDERED',
  COLLECTED: 'COLLECTED',
  RESULTED: 'RESULTED',
  CANCELLED: 'CANCELLED',
} as const;
export type LabOrderStatus = (typeof LabOrderStatus)[keyof typeof LabOrderStatus];

/** LAB-09 — abnormal values are coloured red automatically against the normal range. */
export const LabResultFlag = {
  NORMAL: 'NORMAL',
  LOW: 'LOW',
  HIGH: 'HIGH',
  CRITICAL_LOW: 'CRITICAL_LOW',
  CRITICAL_HIGH: 'CRITICAL_HIGH',
} as const;
export type LabResultFlag = (typeof LabResultFlag)[keyof typeof LabResultFlag];

export const LabTestKind = {
  /** LAB-07 — structured, with parameters, units and normal ranges. */
  STRUCTURED: 'STRUCTURED',
  /** LAB-10 — anything else: attachment plus a text note. */
  ATTACHMENT_ONLY: 'ATTACHMENT_ONLY',
  IMAGING: 'IMAGING',
} as const;
export type LabTestKind = (typeof LabTestKind)[keyof typeof LabTestKind];

// ---------------------------------------------------------------------------
// Notifications (in-app bell only — INV-100)
// ---------------------------------------------------------------------------

export const NotificationKind = {
  STOCK_ALERT: 'STOCK_ALERT',
  APPOINTMENTS_NEED_CLOSURE: 'APPOINTMENTS_NEED_CLOSURE',
  BACKUP_PROBLEM: 'BACKUP_PROBLEM',
  PATIENT_CHECKED_IN: 'PATIENT_CHECKED_IN',
  WAIT_TIME_EXCEEDED: 'WAIT_TIME_EXCEEDED',
  STOCKTAKE_ADJUSTMENTS_DIGEST: 'STOCKTAKE_ADJUSTMENTS_DIGEST',
  PENDING_SETTLEMENT: 'PENDING_SETTLEMENT',
  RECALL_DUE: 'RECALL_DUE',
  TASK_ASSIGNED: 'TASK_ASSIGNED',
  COMMISSION_STATEMENT_READY: 'COMMISSION_STATEMENT_READY',
  HR_REQUEST_SUBMITTED: 'HR_REQUEST_SUBMITTED',
  HR_REQUEST_DECIDED: 'HR_REQUEST_DECIDED',
} as const;
export type NotificationKind = (typeof NotificationKind)[keyof typeof NotificationKind];

export const NotificationSeverity = {
  INFO: 'INFO',
  WARNING: 'WARNING',
  CRITICAL: 'CRITICAL',
} as const;
export type NotificationSeverity =
  (typeof NotificationSeverity)[keyof typeof NotificationSeverity];
